import type { ReactNode } from "react";
import type { ZodTypeAny } from "zod";
import { z } from "@/zod";
import { NUMBER_SCHEMA } from "@/schemas/commonFields/numberSchema";
import { unwrapZodType } from "@/lib/utils/zod";
import type { Formatters } from "@/lib/hooks/useFormatters";

function isFormatableValue(value: ReactNode) {
  return (
    value == null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

export function formatDetailFieldValue(
  schema: ZodTypeAny,
  field: string,
  value: ReactNode,
  formatters: Formatters,
): ReactNode {
  if (!isFormatableValue(value)) return value;

  const unwrappedSchema = unwrapZodType(schema);
  const shape =
    unwrappedSchema instanceof z.ZodObject
      ? unwrappedSchema.shape
      : unwrappedSchema?._def?.shape?.();
  const zodProp = shape?.[field];
  if (!zodProp) return String(value ?? "");
  const zodType = unwrapZodType(zodProp);

  if (zodType instanceof z.ZodBoolean) {
    return formatters.formatBoolean(value as boolean | undefined);
  }
  if (zodType instanceof z.ZodString) {
    const checks = (zodType as z.ZodString)._def.checks ?? [];
    if (checks.some((c: { kind: string }) => c.kind === "date")) {
      return formatters.formatDate(value as string | Date | null | undefined);
    }
    if (checks.some((c: { kind: string }) => c.kind === "datetime")) {
      return formatters.formatDateTime(value as string | null | undefined);
    }
  }
  if (zodProp?._def?.description === NUMBER_SCHEMA) {
    return formatters.formatNumber(value as number | string | null | undefined);
  }
  return String(value ?? "");
}
